import * as THREE from 'three';
import Stats from 'stats.js';
import { loadCoreAssets, type GameAssets } from './AssetLoader';
import { createGameScene, type GameScene } from './Scene';
import { ChaseCamera } from './Camera';
import { InputHandler, keyCodeToLabel } from './InputHandler';
import type { KeyBindings } from './InputHandler';
import { FlightController } from './FlightController';
import { WingTrails } from './WingTrails';
import { TracerSystem } from './Tracers';
import { MissileSystem } from './MissileSystem';
import { Minimap } from './Minimap';
import { EnemyMarker } from './EnemyMarker';
import { CombatSystem } from './CombatSystem';
import { CollisionSystem } from './CollisionSystem';
import { SpeedLineSystem } from './SpeedLines';
import { FlareSystem } from './FlareSystem';
import { ExplosionSystem } from './Explosion';
import { AudioSystem } from './AudioSystem';
import { HitMarker } from './HitMarker';
import { Portal, isFromPortal, getRefUrl, buildExitUrl, buildReturnUrl } from './Portal';
import { TouchControls, isTouchDevice } from './TouchControls';
import { createSkyRules } from './SkyText';
import { SmokeTrail } from './SmokeTrail';
import type { Role, Team, LeaderboardEntry, KillEvent } from '@shared/types';
import type { SocketClient } from '../net/SocketClient';
import { StateEmitter } from '../net/StateEmitter';
import { RemotePlayerManager, type RemoteEntry } from '../net/RemotePlayerManager';
import { RemoteProjectileManager } from '../net/RemoteProjectiles';
import { DRONE_HITBOX_RADIUS } from '@shared/constants';
import { TOWER_POSITION, DRONE_SPAWN_DISTANCE, DRONE_HITBOX_RADIUS as DHR, FIGHTER_HITBOX_RADIUS as FHR } from '@shared/constants';

// ============================================================
// Main Game class — owns the render loop
// ============================================================

export class Game {
  private renderer!: THREE.WebGLRenderer;
  private gameScene!: GameScene;
  private chaseCamera!: ChaseCamera;
  private inputHandler!: InputHandler;
  private flightController!: FlightController;
  private clock = new THREE.Clock();
  private assets!: GameAssets;
  private running = false;
  private boostBarFill!: HTMLElement;
  private gunBarFill!: HTMLElement;
  private crosshair!: HTMLElement;
  private wingTrails!: WingTrails;
  private tracers!: TracerSystem;
  private missileSystem!: MissileSystem;
  private lockProgress!: HTMLElement;
  private lockCircle!: SVGCircleElement;
  private missilePips!: NodeListOf<HTMLElement>;
  private missileHud!: HTMLElement;
  private minimap!: Minimap;
  private missileArrows: HTMLElement[] = [];
  private flareSystem!: FlareSystem;
  private audio!: AudioSystem;
  private speedLines!: SpeedLineSystem;
  private enemyMarkers: EnemyMarker[] = [];
  private missileWarningEl!: HTMLElement;
  private flarePips!: NodeListOf<HTMLElement>;
  private flareCounter!: HTMLElement;
  private combatSystem!: CombatSystem;
  private collisionSystem!: CollisionSystem;
  private playerAlive = true;
  private respawnTimer = 0;
  private readonly deathCamPos = new THREE.Vector3();
  private readonly deathCamDir = new THREE.Vector3();
  private killFeedEl!: HTMLElement;
  private scoreEl!: HTMLElement | null;
  private leaderboardEl!: HTMLElement;
  private username = 'Player';
  private role!: Role;
  private team!: Team;
  private socketClient: SocketClient | null = null;
  private stateEmitter: StateEmitter | null = null;
  private remotePlayerManager: RemotePlayerManager | null = null;
  private remoteProjectiles: RemoteProjectileManager | null = null;
  private hitMarker!: HitMarker;
  private readonly smokeTrails = new Map<string, SmokeTrail>();
  private localSmoke: SmokeTrail | null = null;
  private exitPortal: Portal | null = null;
  private startPortal: Portal | null = null;
  private touchControls: TouchControls | null = null;
  private readonly killFeedItems: { killEvent: KillEvent; age: number; verb: string }[] = [];
  private oobTimer = 0;         // out-of-bounds countdown (5 → 0 = death)
  private oobWarningEl!: HTMLElement;
  private oobTimerEl!: HTMLElement;
  private oobVignetteEl!: HTMLElement;
  private stats: Stats | null = null;

  async init(role: Role, username = 'Player', socketClient?: SocketClient, team?: Team): Promise<void> {
    this.socketClient = socketClient ?? null;
    this.team = team ?? (role === 'shahed' ? 'drones' : 'fighters');
    this.username = username;
    // ── Renderer ──────────────────────────────────────────
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.8;
    document.body.appendChild(this.renderer.domElement);

    // ── Stats (FPS/MS/MB) — hidden, toggle with ` key ─────
    this.stats = new Stats();
    this.stats.showPanel(0);
    this.stats.dom.style.position = 'absolute';
    this.stats.dom.style.top = '0px';
    this.stats.dom.style.left = '0px';
    this.stats.dom.style.display = 'none';
    document.body.appendChild(this.stats.dom);
    window.addEventListener('keydown', (e) => {
      if (e.key === '`') {
        const vis = this.stats!.dom.style.display;
        this.stats!.dom.style.display = vis === 'none' ? 'block' : 'none';
      }
    });

    // ── Load assets ───────────────────────────────────────
    console.log('[SKYFALL] Loading assets...');
    this.assets = await loadCoreAssets();
    console.log('[SKYFALL] Assets loaded');

    // ── Scene ─────────────────────────────────────────────
    this.gameScene = createGameScene(this.assets);

    // ── Camera ────────────────────────────────────────────
    const aspect = window.innerWidth / window.innerHeight;
    const camDist = role === 'f16' ? 22 : 35;
    const camHeight = role === 'f16' ? 8 : 12;
    this.chaseCamera = new ChaseCamera(aspect, camDist, camHeight);

    // ── Input ─────────────────────────────────────────────
    this.inputHandler = new InputHandler(this.renderer.domElement);

    // Mobile touch controls — auto-detected
    if (isTouchDevice()) {
      this.touchControls = new TouchControls(role);
    }

    // ── Player aircraft ───────────────────────────────────
    const playerModel = role === 'shahed'
      ? this.assets.shahed.clone()
      : this.assets.f16.clone();
    playerModel.name = 'player';

    // Spawn position
    if (role === 'shahed') {
      const angle = Math.random() * Math.PI * 2;
      playerModel.position.set(
        TOWER_POSITION.x + Math.cos(angle) * DRONE_SPAWN_DISTANCE,
        120,
        TOWER_POSITION.z + Math.sin(angle) * DRONE_SPAWN_DISTANCE,
      );
      const dx = TOWER_POSITION.x - playerModel.position.x;
      const dz = TOWER_POSITION.z - playerModel.position.z;
      playerModel.rotation.set(0, Math.atan2(-dx, -dz), 0);
    } else {
      playerModel.position.set(TOWER_POSITION.x + 200, 150, TOWER_POSITION.z + 200);
      playerModel.lookAt(TOWER_POSITION.x, 150, TOWER_POSITION.z);
    }

    this.gameScene.scene.add(playerModel);
    this.flightController = new FlightController(playerModel, role);

    // ── Wing trails ─────────────────────────────────────
    const wingSpan = role === 'shahed' ? 5.25 : 3;
    const trailZ = role === 'shahed' ? 3 : 5;
    this.wingTrails = new WingTrails(
      this.gameScene.scene,
      new THREE.Vector3(-wingSpan, 0, trailZ),
      new THREE.Vector3(wingSpan, 0, trailZ),
      0.4,
    );

    // ── Debug: expose to console for tuning ──────���────────
    (window as unknown as Record<string, unknown>).__skyfall = {
      player: playerModel,
      scene: this.gameScene.scene,
      camera: this.chaseCamera.camera,
      flight: this.flightController,
    };
    console.log('[SKYFALL] Debug: window.__skyfall available');

    // ── Tracers + Missiles (F-16 only) ─────────────────────
    this.tracers = new TracerSystem(this.gameScene.scene);
    this.tracers.setCollisionSystem(this.collisionSystem);
    this.missileSystem = new MissileSystem(this.gameScene.scene);
    this.missileSystem.setCollisionSystem(this.collisionSystem);
    this.role = role;

    // ── Flare system (drone only) ──────────────────────────
    if (role === 'shahed') {
      this.flareSystem = new FlareSystem(
        this.gameScene.scene,
        new ExplosionSystem(this.gameScene.scene),
      );
    }

    // ── Collision system ─────────────────────────────────
    this.collisionSystem = new CollisionSystem();
    // No city chunks — above the clouds
    this.collisionSystem.addCollidable(
      this.gameScene.scene.getObjectByName('target-ship')!
    );

    // ── Combat system ────────────────────────────────────
    this.combatSystem = new CombatSystem(this.gameScene.scene);
    this.combatSystem.setKillerName(username);
    this.killFeedEl = document.getElementById('kill-feed-entries')!;
    this.scoreEl = document.getElementById('score');
    this.leaderboardEl = document.getElementById('leaderboard-entries')!;

    // ── Minimap + Enemy markers ───────────────────────────
    this.minimap = new Minimap();
    this.speedLines = new SpeedLineSystem(this.gameScene.scene);
    this.missileWarningEl = document.getElementById('missile-warning')!;
    this.oobWarningEl = document.getElementById('oob-warning')!;
    this.oobTimerEl = document.querySelector('#oob-warning .oob-timer')!;
    this.oobVignetteEl = document.getElementById('oob-vignette')!;
    this.flareCounter = document.getElementById('flare-counter')!;
    this.flarePips = document.querySelectorAll('.flare-pip') as NodeListOf<HTMLElement>;

    // ── HUD elements ──────────────────────────────────────
    this.boostBarFill = document.getElementById('boost-bar-fill')!;
    this.gunBarFill = document.getElementById('gun-bar-fill')!;
    this.crosshair = document.getElementById('crosshair')!;
    this.lockProgress = document.getElementById('lock-progress')!;
    this.lockCircle = document.querySelector('#lock-progress circle')!;
    this.missilePips = document.querySelectorAll('.missile-pip') as NodeListOf<HTMLElement>;
    this.missileHud = document.getElementById('missile-hud')!;

    if (role === 'shahed') {
      this.crosshair.style.display = 'none';
      document.getElementById('gun-bar-container')!.style.display = 'none';
      document.getElementById('gun-label')!.style.display = 'none';
      this.flareCounter.style.display = 'flex';
    } else {
      document.getElementById('boost-bar-container')!.style.display = 'none';
      document.getElementById('boost-label')!.style.display = 'none';
      document.getElementById('gun-bar-container')!.style.display = 'block';
      document.getElementById('gun-label')!.style.display = 'block';
      this.crosshair.style.display = 'block';
      this.missileHud.style.display = 'flex';
    }

    // ── Audio ────────────────────────────────────────────
    this.audio = new AudioSystem();
    this.audio.init();
    this.hitMarker = new HitMarker();
    this.localSmoke = new SmokeTrail(this.gameScene.scene);

    // ── Settings ────────────────────────────────────────
    this.initSettings();

    // ── Sky rules ──────────────────────────────────────────
    createSkyRules(this.gameScene.scene, TOWER_POSITION.x, TOWER_POSITION.z);

    // ── Vibe Jam Portals ───────────────────────────────────
    // Exit portal (green) — always present, near the ship
    this.exitPortal = new Portal(
      this.gameScene.scene,
      new THREE.Vector3(TOWER_POSITION.x + 150, 80, TOWER_POSITION.z - 150),
      0x00ff00,
      'VIBE JAM PORTAL',
    );

    // Start portal (red) — only if player came from another game
    if (isFromPortal()) {
      const refUrl = getRefUrl();
      if (refUrl) {
        this.startPortal = new Portal(
          this.gameScene.scene,
          new THREE.Vector3(TOWER_POSITION.x - 150, 80, TOWER_POSITION.z + 150),
          0xff0000,
          'RETURN PORTAL',
        );
      }
    }

    // ── Resize ────────────────────────────────────────────
    window.addEventListener('resize', () => this.onResize());

    // ── Networking ───────────────────────────────────────────
    if (this.socketClient) {
      // Track enemy markers per remote player for cleanup
      const remoteMarkers = new Map<string, EnemyMarker>();

      // Remote player rendering with integration callbacks
      this.remotePlayerManager = new RemotePlayerManager(
        this.gameScene.scene,
        this.assets,
        this.socketClient.id,
        {
          onSpawn: (id: string, entry: RemoteEntry) => {
            const isEnemy = (entry.role === 'shahed' && this.role === 'f16')
              || (entry.role === 'f16' && this.role === 'shahed');

            const markerColor = isEnemy ? 0xff4444 : 0x44aaff;
            const minimapColor = isEnemy ? '#f44' : '#4af';
            const hitboxRadius = entry.role === 'shahed' ? DRONE_HITBOX_RADIUS : 4;

            // Add to minimap
            this.minimap.addEntity(entry.mesh, minimapColor);

            // Marker above player (red for enemies, blue for allies)
            const marker = new EnemyMarker(entry.mesh, this.gameScene.scene, markerColor, entry.username);
            marker.marker.userData.targetMesh = entry.mesh;
            this.enemyMarkers.push(marker);
            remoteMarkers.set(id, marker);

            // Combat target + missile lock (enemies only)
            if (isEnemy) {
              this.combatSystem.addTarget(
                entry.mesh,
                entry.username,
                hitboxRadius,
                () => {},
                () => {},
                true,
              );

              if (this.role === 'f16' && entry.role === 'shahed') {
                this.missileSystem.addTarget(entry.mesh);
              }
            }
          },
          onRemove: (id: string, entry: RemoteEntry) => {
            // Clean up minimap
            this.minimap.removeEntity(entry.mesh);

            // Clean up enemy marker
            const marker = remoteMarkers.get(id);
            if (marker) {
              const idx = this.enemyMarkers.indexOf(marker);
              if (idx !== -1) this.enemyMarkers.splice(idx, 1);
              marker.dispose(this.gameScene.scene);
              remoteMarkers.delete(id);
            }

            // Clean up combat target
            this.combatSystem.removeTarget(entry.mesh);

            // Clean up missile target
            this.missileSystem.removeTarget(entry.mesh);
          },
        },
      );

      // Remote projectile rendering (tracers + missiles from other players)
      this.remoteProjectiles = new RemoteProjectileManager(
        this.gameScene.scene,
        this.remotePlayerManager,
        this.socketClient.id,
      );
      this.remoteProjectiles.setLocalPlayerMesh(this.flightController.mesh);
      if (this.flareSystem) {
        this.remoteProjectiles.setFlareSystem(this.flareSystem);
      }
      this.remoteProjectiles.setExplosions(new ExplosionSystem(this.gameScene.scene));
      this.remoteProjectiles.setCallbacks({
        onMissileHitLocal: (_missileId, position) => {
          if (this.playerAlive) {
            this.chaseCamera.shake(5);
            this.audio.playExplosion();
            this.playerDeath(`${this.username} was hit by a missile!`);
          }
        },
      });

      // Listen for state broadcasts
      this.socketClient.on('game:remoteStates', (states) => {
        this.remotePlayerManager?.onRemoteStates(states);

        // Sync alive state for combat targets + smoke trails
        const remotes = this.remotePlayerManager?.getRemotes();
        if (remotes) {
          for (const [id, state] of Object.entries(states)) {
            const entry = remotes.get(id);
            if (entry) {
              this.combatSystem.setTargetAlive(entry.mesh, state.alive);
            }
            // Clear smoke on respawn (full health)
            if (state.alive && state.health >= 100) {
              const trail = this.smokeTrails.get(id);
              if (trail) trail.setIntensity(0);
            }
          }
        }

        // Update remote firing states for tracer rendering
        const firingIds = new Set<string>();
        for (const [id, state] of Object.entries(states)) {
          if (state.isFiring) firingIds.add(id);
        }
        this.remoteProjectiles?.updateFiringStates(firingIds);
      });

      // Listen for player leaving
      this.socketClient.on('game:playerLeft', (playerId) => {
        this.remotePlayerManager?.removeById(playerId);
      });

      // Listen for server leaderboard
      this.socketClient.on('game:leaderboard', (entries) => {
        this.leaderboardEl.innerHTML = entries
          .map((e) => {
            const color = e.team === this.team ? '#4af' : '#f44';
            return `<div style="color:${color};margin-bottom:4px">${e.username} — ${e.score}</div>`;
          })
          .join('');
      });

      // Listen for damage (when local player is hit by someone else)
      this.socketClient.on('game:damage', (targetId, newHealth, _attackerId) => {
        // Local player got hit
        if (targetId === this.socketClient!.id && this.playerAlive) {
          // Progressive smoke on self
          if (this.localSmoke && newHealth > 0) {
            const damageFraction = 1 - (newHealth / 100);
            this.localSmoke.setIntensity(damageFraction);
          }
          if (newHealth <= 0) {
            this.localSmoke?.setIntensity(0);
            this.combatSystem.spawnExplosion(this.flightController.mesh.position.clone());
            this.playerDeath(`${this.username} was shot down!`);
          }
        }

        // Remote player — update smoke trail + handle death
        if (targetId !== this.socketClient!.id) {
          // Progressive smoke — more damage = more smoke
          const trail = this.smokeTrails.get(targetId);
          if (trail && newHealth > 0) {
            const damageFraction = 1 - (newHealth / 100);
            trail.setIntensity(damageFraction);
          }

          // Remote player killed — spawn explosion + immediately mark as dead
          if (newHealth <= 0) {
            const remotes = this.remotePlayerManager?.getRemotes();
            const entry = remotes?.get(targetId);
            if (entry) {
              this.combatSystem.spawnExplosion(entry.mesh.position.clone());
              this.combatSystem.setTargetAlive(entry.mesh, false);
              entry.mesh.visible = false;
              this.audio.playExplosion();
            }
            if (trail) trail.setIntensity(0);
          }
        }
      });

      // Listen for kills (shared kill feed)
      this.socketClient.on('game:kill', (killEvent) => {
        this.killFeedItems.unshift({ killEvent, age: 0, verb: this.getKillVerb() });
        if (this.killFeedItems.length > 6) this.killFeedItems.pop();
      });

      // Listen for remote missile launches
      this.socketClient.on('game:missileLaunched', (event) => {
        this.remoteProjectiles?.onMissileLaunched(event);
      });

      // Listen for remote missile explosions
      this.socketClient.on('game:missileExploded', (missileId) => {
        this.remoteProjectiles?.onMissileExploded(missileId);
      });

      // Listen for mid-air collision (server confirmed — both players die)
      this.socketClient.on('game:midairCollision', (droneId, fighterId) => {
        const myId = this.socketClient!.id;

        // Explode local player if involved
        if ((myId === droneId || myId === fighterId) && this.playerAlive) {
          this.combatSystem.spawnExplosion(this.flightController.mesh.position.clone());
          this.audio.playExplosion();
          this.chaseCamera.shake(5);
          this.playerDeath(`${this.username} mid-air collision!`);
        }

        // Explode remote player involved
        const remotes = this.remotePlayerManager?.getRemotes();
        if (remotes) {
          for (const rid of [droneId, fighterId]) {
            if (rid === myId) continue;
            const entry = remotes.get(rid);
            if (entry) {
              this.combatSystem.spawnExplosion(entry.mesh.position.clone());
            }
          }
        }
      });

      // Hook local missile launches to broadcast to other players
      let missileCounter = 0;
      this.missileSystem.onLaunch = (target, position, direction) => {
        const targetId = this.extractRemoteId(target);
        if (!targetId || !this.socketClient) return;

        const missileId = `${this.socketClient.id}-m${missileCounter++}`;
        this.socketClient.sendMissileLaunch({
          shooterId: this.socketClient.id,
          missileId,
          targetId,
          position: { x: position.x, y: position.y, z: position.z },
          direction: { x: direction.x, y: direction.y, z: direction.z },
        });
      };

      // Send local state at 20Hz
      this.stateEmitter = new StateEmitter(
        this.socketClient,
        this.flightController,
        role,
        this.team,
        username,
      );
      this.stateEmitter.start();

      // ── Reconnection overlay ────────────────────────────
      this.setupReconnectOverlay();
    }

    // ── Start ─────────────────────────────────────────────
    this.running = true;
    this.clock.start();
    this.loop();

    console.log(`[SKYFALL] Game started as ${role} — click to capture mouse`);
    console.log('[SKYFALL] Controls: W/S pitch, A/D roll, Mouse aim, Space/Shift altitude, E boost');
    console.log('[SKYFALL] Press ` to toggle FPS stats');
    this.renderer.info.autoReset = false;
    // Log renderer stats after first frame
    requestAnimationFrame(() => {
      const info = this.renderer.info;
      console.log(`[SKYFALL] Render info — draw calls: ${info.render.calls}, triangles: ${info.render.triangles}, textures: ${info.memory.textures}, geometries: ${info.memory.geometries}`);
      this.renderer.info.autoReset = true;
    });
  }

  private loop = (): void => {
    if (!this.running) return;
    requestAnimationFrame(this.loop);
    this.stats?.begin();

    const dt = Math.min(this.clock.getDelta(), 0.05);

    // Respawn timer — death camera drifts forward
    if (!this.playerAlive) {
      this.respawnTimer -= dt;
      if (this.respawnTimer <= 0) {
        this.playerAlive = true;
        this.flightController.mesh.visible = true;
        this.respawnPlayer();
      } else {
        // Static death camera
        // Animate water
        const waterUniforms = this.gameScene.water.material.uniforms;
        if (waterUniforms['time']) waterUniforms['time'].value += dt;
        this.combatSystem.update(dt);

        this.updateHUD(dt);
        this.renderer.render(this.gameScene.scene, this.chaseCamera.camera);
        this.stats?.end();
      }
      return;
    }

    // Update — use touch controls on mobile, keyboard on desktop
    const input = this.touchControls
      ? this.touchControls.getState()
      : this.inputHandler.getState();
    this.flightController.update(input, dt);
    if (this.flightController.didBarrelRoll) this.audio.playBarrelRoll();
    this.wingTrails.update(this.flightController.mesh);
    this.chaseCamera.update(this.flightController.mesh, dt);

    // ── Out-of-bounds check ────────────────────────────────
    const px = this.flightController.mesh.position.x - TOWER_POSITION.x;
    const pz = this.flightController.mesh.position.z - TOWER_POSITION.z;
    const distFromCenter = Math.sqrt(px * px + pz * pz);

    if (distFromCenter >= 2500) {
      if (this.oobTimer <= 0) this.oobTimer = 10; // start countdown
      this.oobTimer -= dt;
      this.oobWarningEl.style.display = 'block';
      this.oobVignetteEl.style.display = 'block';
      this.oobTimerEl.textContent = Math.max(0, Math.ceil(this.oobTimer)).toString();
      this.audio.updateMissileWarning(1); // reuse warning beep

      if (this.oobTimer <= 0) {
        this.combatSystem.spawnExplosion(this.flightController.mesh.position.clone());
        this.addCrashFeedEntry();
        this.playerDeath(`${this.username} left the combat zone`);
        this.oobWarningEl.style.display = 'none';
        this.oobVignetteEl.style.display = 'none';
        this.oobTimer = 0;
      }
    } else {
      if (this.oobTimer > 0) {
        this.oobTimer = 0;
        this.oobWarningEl.style.display = 'none';
        this.oobVignetteEl.style.display = 'none';
        this.audio.updateMissileWarning(0);
      }
    }

    // Building/ground collision
    const crashSurface = this.collisionSystem.check(this.flightController.mesh);
    if (crashSurface) {
      // Drones score when hitting the ship instead of crashing
      if (this.role === 'shahed' && crashSurface === 'the ship') {
        if (this.socketClient) {
          this.socketClient.sendDroneScore();
        } else {
          this.combatSystem.scoreDronePoint();
        }
        // Add styled kill feed entry for ship hit
        this.killFeedItems.unshift({
          killEvent: {
            killerId: this.socketClient?.id ?? '',
            killerName: this.username,
            killerTeam: this.team,
            victimId: 'ship',
            victimName: 'Aircraft Carrier',
            victimTeam: 'fighters',
            weapon: 'crash',
          },
          age: 0,
          verb: this.getShipVerb(),
        });
        if (this.killFeedItems.length > 6) this.killFeedItems.pop();
        this.playerDeath(`${this.username} hit the ship! +1`);
      } else {
        if (crashSurface === 'the water') this.audio.playWaterCrash();
        this.addCrashFeedEntry();
        this.playerDeath(`${this.username} crashed`);
      }
    }

    // ── Mid-air collision (drone vs F-16) ─────────────────
    if (this.remotePlayerManager && this.playerAlive) {
      const myPos = this.flightController.mesh.position;
      const myRadius = this.role === 'shahed' ? DHR : FHR;

      for (const [id, entry] of this.remotePlayerManager.getRemotes()) {
        if (!entry.mesh.visible) continue;
        // Only collide with enemy type
        const isEnemy = (this.role === 'shahed' && entry.role === 'f16')
          || (this.role === 'f16' && entry.role === 'shahed');
        if (!isEnemy) continue;

        const theirRadius = entry.role === 'shahed' ? DHR : FHR;
        const dist = myPos.distanceTo(entry.mesh.position);

        if (dist < myRadius + theirRadius + 3) {
          // Report to server — server kills both and broadcasts
          this.socketClient?.sendMidairCollision(id);
          break;
        }
      }
    }

    // ── Portal updates + collision ────────────────────────
    if (this.exitPortal) {
      this.exitPortal.update();
      if (this.playerAlive && this.exitPortal.checkCollision(this.flightController.mesh.position)) {
        const gameUrl = window.location.origin + window.location.pathname;
        window.location.href = buildExitUrl(this.username, gameUrl);
      }
    }
    if (this.startPortal) {
      this.startPortal.update();
      if (this.playerAlive && this.startPortal.checkCollision(this.flightController.mesh.position)) {
        const refUrl = getRefUrl();
        if (refUrl) {
          window.location.href = buildReturnUrl(refUrl);
        }
      }
    }

    // Audio — engine
    this.audio.updateEngine(
      this.flightController.isBoosting ? 200 : 100,
      this.flightController.isBoosting,
    );

    // Animate water
    const waterUniforms = this.gameScene.water.material.uniforms;
    if (waterUniforms['time']) waterUniforms['time'].value += dt;

    // Update remote players (interpolate positions)
    if (this.remotePlayerManager) {
      this.remotePlayerManager.update();
    }

    // Update remote projectiles (tracers + missiles from other players)
    if (this.remoteProjectiles) {
      this.remoteProjectiles.update(dt);
    }

    // Combat system
    this.combatSystem.update(dt);

    // Update tracers + missiles (F-16)
    if (this.role === 'f16') {
      const lockMode = input.lockMode;
      const tracerHit = (pos: THREE.Vector3) => {
        const hit = this.combatSystem.checkTracerHit(pos);
        if (hit) {
          this.chaseCamera.shake(0.5);
          this.audio.playHitConfirm();
          this.audio.playExplosion();
          this.hitMarker.show();

          // Report hit to server
          if (this.socketClient) {
            const targetId = this.findHitTargetId(pos);
            if (targetId) {
              this.socketClient.sendHitReport({
                attackerId: this.socketClient.id,
                targetId,
                weapon: 'gun',
                position: { x: pos.x, y: pos.y, z: pos.z },
              });
            }
          }
        }
        return hit;
      };
      const missileHit = (pos: THREE.Vector3, target: THREE.Object3D) => {
        const hit = this.combatSystem.checkMissileHit(pos, target);
        if (hit) {
          this.chaseCamera.shake(2);
          this.audio.playExplosion();
          this.hitMarker.show();

          // Report hit to server
          if (this.socketClient) {
            const targetId = this.extractRemoteId(target);
            if (targetId) {
              this.socketClient.sendHitReport({
                attackerId: this.socketClient.id,
                targetId,
                weapon: 'missile',
                position: { x: pos.x, y: pos.y, z: pos.z },
              });
            }
          }
        }
        return hit;
      };

      // Gun sound
      this.audio.updateGun(this.flightController.isFiring && !lockMode, dt);

      // Gun fires only when NOT in lock mode
      if (!lockMode) {
        this.tracers.update(this.flightController.mesh, this.flightController.isFiring, dt, tracerHit);
      } else {
        this.tracers.update(this.flightController.mesh, false, dt, tracerHit);
      }

      // Missiles
      this.missileSystem.update(
        this.flightController.mesh,
        this.chaseCamera.camera,
        lockMode,
        input.up,
        dt,
        missileHit,
        (pos) => this.combatSystem.spawnExplosion(pos),
      );

      // Crosshair mode
      if (lockMode) {
        this.crosshair.classList.add('lock-mode');
        this.crosshair.classList.remove('firing');
        if (this.missileSystem.isLocked) {
          this.crosshair.classList.add('locked');
        } else {
          this.crosshair.classList.remove('locked');
        }
      } else {
        this.crosshair.classList.remove('lock-mode', 'locked');
      }

      // Lock progress circle
      if (lockMode && this.missileSystem.isLocking && !this.missileSystem.isLocked) {
        this.lockProgress.style.display = 'block';
        const circumference = 157; // 2 * PI * 25
        const offset = circumference * (1 - this.missileSystem.lockProgress);
        this.lockCircle.style.strokeDashoffset = `${offset}`;
      } else if (lockMode && this.missileSystem.isLocked) {
        this.lockProgress.style.display = 'block';
        this.lockCircle.style.strokeDashoffset = '0';
        this.lockCircle.style.stroke = 'rgba(255, 0, 0, 0.9)';
      } else {
        this.lockProgress.style.display = 'none';
        this.lockCircle.style.stroke = 'rgba(255, 60, 60, 0.6)';
      }

      // Missile ammo pips
      for (let i = 0; i < this.missilePips.length; i++) {
        if (i < this.missileSystem.currentAmmo) {
          this.missilePips[i].classList.remove('empty');
        } else {
          this.missilePips[i].classList.add('empty');
        }
      }
    }

    // ── Speed lines (both roles) ──────────────────────────
    this.speedLines.update(this.flightController.mesh, this.flightController.isBoosting ? 200 : 110, dt);

    // ── FOV widening during boost (drone) ───────────────
    if (this.role === 'shahed') {
      const targetFov = this.flightController.isBoosting ? 85 : 70;
      this.chaseCamera.camera.fov += (targetFov - this.chaseCamera.camera.fov) * 5 * dt;
      this.chaseCamera.camera.updateProjectionMatrix();
    }

    // ── Missile warning + flare (drone) ───────────────────
    if (this.role === 'shahed') {
      // Count from both local missile system and remote missiles
      let incomingCount = this.missileSystem.getMissilesTargeting(this.flightController.mesh);
      const remoteIncoming = this.remoteProjectiles?.getIncomingMissileCount() ?? 0;
      incomingCount += remoteIncoming;

      const beingLocked = this.missileSystem.isBeingLockedOn(this.flightController.mesh);

      if (incomingCount > 0) {
        this.missileWarningEl.style.display = 'block';
        this.missileWarningEl.textContent = `MISSILE INCOMING (${incomingCount})`;
      } else if (beingLocked) {
        this.missileWarningEl.style.display = 'block';
        this.missileWarningEl.textContent = 'LOCKED';
      } else {
        this.missileWarningEl.style.display = 'none';
      }

      // Keep warning audio playing if OOB or missiles incoming
      this.audio.updateMissileWarning(incomingCount > 0 || this.oobTimer > 0 ? 1 : 0);

      // Show remote missile positions on minimap + directional arrows
      const remoteMissilePositions = this.remoteProjectiles?.getMissilesTargetingLocal() ?? [];
      for (const pos of remoteMissilePositions) {
        this.minimap.addInstantBlip(pos, '#ff2200');
      }
      this.updateMissileArrows(remoteMissilePositions);

      // Flare — deploy physical countermeasures
      if (this.flightController.didFlare && this.flareSystem) {
        this.flareSystem.deploy(this.flightController.mesh);
        this.audio.playFlare();
      }

      // Update flares
      if (this.flareSystem) {
        this.flareSystem.update(this.flightController.mesh, dt);
      }

      // Flare pips
      for (let i = 0; i < this.flarePips.length; i++) {
        if (i < this.flightController.flaresRemaining) {
          this.flarePips[i].classList.remove('empty');
        } else {
          this.flarePips[i].classList.add('empty');
        }
      }
    }

    // ── Camera shake on kills ───────────────────────────
    // (triggered by combat system — check for recent kills)

    // Update HUD
    if (this.role === 'shahed') {
      this.boostBarFill.style.width = `${this.flightController.boostFraction * 100}%`;
      if (this.flightController.isBoosting) {
        this.boostBarFill.classList.add('boosting');
      } else {
        this.boostBarFill.classList.remove('boosting');
      }
    } else {
      // Gun heat bar (fills up as gun heats)
      this.gunBarFill.style.width = `${this.flightController.gunHeatFraction * 100}%`;
      if (this.flightController.isFiring) {
        this.gunBarFill.classList.add('firing');
        this.crosshair.classList.add('firing');
      } else {
        this.gunBarFill.classList.remove('firing');
        this.crosshair.classList.remove('firing');
      }
      // Overheated state
      if (this.flightController.gunHeatFraction >= 1) {
        this.gunBarFill.classList.add('overheated');
      } else {
        this.gunBarFill.classList.remove('overheated');
      }
    }

    // Player markers
    for (const marker of this.enemyMarkers) {
      marker.update(dt, this.chaseCamera.camera);
    }

    // Minimap
    this.minimap.update(this.flightController.mesh, dt);

    // Hit marker
    this.hitMarker.update(dt);

    // Local smoke trail (when damaged)
    if (this.localSmoke) {
      this.localSmoke.update(this.flightController.mesh, dt);
    }

    // Smoke trails on damaged remote players
    if (this.remotePlayerManager) {
      for (const [id, entry] of this.remotePlayerManager.getRemotes()) {
        let trail = this.smokeTrails.get(id);
        if (!trail) {
          trail = new SmokeTrail(this.gameScene.scene);
          this.smokeTrails.set(id, trail);
        }
        trail.update(entry.mesh, dt);
      }
      // Clean up trails for removed players
      for (const [id, trail] of this.smokeTrails) {
        if (!this.remotePlayerManager.getRemotes().has(id)) {
          trail.dispose();
          this.smokeTrails.delete(id);
        }
      }
    }

    this.updateHUD(dt);

    // Render
    this.renderer.render(this.gameScene.scene, this.chaseCamera.camera);
    this.stats?.end();
  };

  /**
   * Extract socket ID from a remote mesh (named "remote-{id}").
   */
  private extractRemoteId(obj: THREE.Object3D): string | null {
    const name = obj.name;
    if (name.startsWith('remote-')) return name.slice(7);
    // Walk up parents (missile target might be a child)
    if (obj.parent && obj.parent.name.startsWith('remote-')) return obj.parent.name.slice(7);
    return null;
  }

  /**
   * Find which remote player was closest to a tracer hit position.
   */
  private findHitTargetId(pos: THREE.Vector3): string | null {
    if (!this.remotePlayerManager) return null;
    let closest: string | null = null;
    let closestDist = Infinity;
    for (const [id, entry] of this.remotePlayerManager.getRemotes()) {
      const dist = pos.distanceTo(entry.mesh.position);
      if (dist < closestDist) {
        closestDist = dist;
        closest = id;
      }
    }
    return closest;
  }

  private playerDeath(reason: string): void {
    if (!this.playerAlive) return;
    this.playerAlive = false;
    this.respawnTimer = 4;
    this.combatSystem.spawnExplosion(this.flightController.mesh.position.clone());
    this.chaseCamera.shake(5);
    this.audio.playExplosion();
    this.audio.updateMissileWarning(0);
    this.deathCamPos.copy(this.chaseCamera.camera.position);
    this.flightController.mesh.getWorldDirection(this.deathCamDir);
    this.flightController.mesh.visible = false;
  }

  private respawnPlayer(): void {
    this.oobTimer = 0;
    this.oobWarningEl.style.display = 'none';
    this.oobVignetteEl.style.display = 'none';
    this.localSmoke?.setIntensity(0);
    this.flightController.resetOnRespawn();
    const mesh = this.flightController.mesh;
    const angle = Math.random() * Math.PI * 2;
    if (this.role === 'shahed') {
      mesh.position.set(
        TOWER_POSITION.x + Math.cos(angle) * DRONE_SPAWN_DISTANCE,
        120,
        TOWER_POSITION.z + Math.sin(angle) * DRONE_SPAWN_DISTANCE,
      );
      const dx = TOWER_POSITION.x - mesh.position.x;
      const dz = TOWER_POSITION.z - mesh.position.z;
      mesh.rotation.set(0, Math.atan2(-dx, -dz), 0);
    } else {
      mesh.position.set(
        TOWER_POSITION.x + Math.cos(angle) * 300,
        150,
        TOWER_POSITION.z + Math.sin(angle) * 300,
      );
      mesh.lookAt(TOWER_POSITION.x, 150, TOWER_POSITION.z);
    }
  }

  private updateHUD(_dt: number): void {
    if (!this.socketClient) {
      // Offline: use local scores
      const scores = this.combatSystem.scores;
      const myScore = this.role === 'f16' ? scores.fighter : scores.drone;
      const enemyScore = this.role === 'f16' ? scores.drone : scores.fighter;

      if (this.scoreEl) {
        this.scoreEl.textContent = `SCORE: ${myScore}`;
      }

      const allyLabel = this.role === 'f16' ? 'F-16' : 'Drone';
      const enemyLabel = this.role === 'f16' ? 'Drone' : 'F-16';
      this.leaderboardEl.innerHTML =
        `<div style="color:#4af;margin-bottom:4px">${this.username} (${allyLabel}) — ${myScore}</div>` +
        `<div style="color:#f44;margin-bottom:4px">${enemyLabel} — ${enemyScore}</div>`;
    } else {
      // Online: score display driven entirely by server leaderboard
      // Hide local score counter — leaderboard has everything
      if (this.scoreEl) {
        this.scoreEl.style.display = 'none';
      }
    }

    // Kill feed — structured with team colors
    // Age out items
    for (let i = this.killFeedItems.length - 1; i >= 0; i--) {
      this.killFeedItems[i].age += _dt;
      if (this.killFeedItems[i].age >= 8) {
        this.killFeedItems.splice(i, 1);
      }
    }

    if (this.killFeedItems.length === 0) {
      if (this.killFeedEl.innerHTML !== '') this.killFeedEl.innerHTML = '';
      return;
    }

    const localTeam = this.team;
    this.killFeedEl.innerHTML = this.killFeedItems.map((item) => {
      const opacity = Math.max(0, 1 - item.age / 8);
      const ke = item.killEvent;
      const killerColor = ke.killerTeam === localTeam ? '#4af' : '#f44';
      const victimColor = ke.victimTeam === localTeam ? '#4af' : '#f44';

      // Ship crash
      if (ke.victimId === 'ship') {
        return `<div style="opacity:${opacity};margin-bottom:4px;line-height:1.4">` +
          `<span style="color:${killerColor};font-weight:bold">${ke.killerName}</span> ` +
          `<span style="color:#fff">${item.verb}</span> ` +
          `<span style="color:${victimColor};font-weight:bold">Aircraft Carrier</span></div>`;
      }

      // Mid-air collision
      if (ke.victimId === 'midair') {
        return `<div style="opacity:${opacity};margin-bottom:4px;line-height:1.4">` +
          `<span style="color:${killerColor};font-weight:bold">${ke.killerName}</span> ` +
          `<span style="color:#fff">${item.verb}</span> ` +
          `<span style="color:${victimColor};font-weight:bold">${ke.victimName}</span></div>`;
      }

      // Self crash
      if (ke.victimId === 'self') {
        return `<div style="opacity:${opacity};margin-bottom:4px;line-height:1.4">` +
          `<span style="color:${killerColor};font-weight:bold">${ke.killerName}</span> ` +
          `<span style="color:#fff">crashed</span> ` +
          `<span style="color:#888">${item.verb}</span></div>`;
      }

      // Normal kill
      const weaponLabel = ke.weapon === 'missile' ? 'a missile' : ke.weapon === 'gun' ? 'the gun' : 'impact';
      return `<div style="opacity:${opacity};margin-bottom:4px;line-height:1.4">` +
        `<span style="color:${killerColor};font-weight:bold">${ke.killerName}</span> ` +
        `<span style="color:#fff">${item.verb}</span> ` +
        `<span style="color:${victimColor};font-weight:bold">${ke.victimName}</span> ` +
        `<span style="color:#888">with ${weaponLabel}</span></div>`;
    }).join('');
  }

  private readonly killVerbs = [
    'obliterated', 'absolutely destroyed', 'vaporized', 'annihilated',
    'wrecked', 'demolished', 'smoked', 'deleted', 'evaporated',
    'sent to the shadow realm', 'turned into confetti',
    'yeeted', 'uninstalled', 'erased from existence',
    'made short work of', 'bodied', 'folded',
    'put down', 'clapped', 'sent to orbit',
  ];

  private getKillVerb(): string {
    return this.killVerbs[Math.floor(Math.random() * this.killVerbs.length)];
  }

  private readonly shipVerbs = [
    'slammed into', 'crashed into', 'nose-dived into',
    'rammed', 'kamikazed into', 'delivered itself to',
    'went full send into', 'face-planted into',
    'made a special delivery to', 'gave a warm hug to',
    'introduced themselves to', 'became one with',
  ];

  private getShipVerb(): string {
    return this.shipVerbs[Math.floor(Math.random() * this.shipVerbs.length)];
  }

  private readonly midairVerbs = [
    'flew straight into', 'crashed head-first into',
    'got intimate with', 'face-checked',
    'played chicken with', 'had a meeting with',
    'high-fived', 'kissed', 'body-slammed',
    'traded paint with', 'merged with',
    'failed to dodge', 'became best friends with',
  ];

  private getMidairVerb(): string {
    return this.midairVerbs[Math.floor(Math.random() * this.midairVerbs.length)];
  }

  private readonly crashVerbs = [
    'pathetically', 'like a noob', 'embarrassingly',
    'like an absolute donkey', 'in the dumbest way possible',
    'with zero dignity', 'like a wet napkin',
    'spectacularly', 'in spectacular fashion',
    'like they forgot how to fly', 'and nobody was surprised',
    'again', 'LOL', 'into the void',
  ];

  private addCrashFeedEntry(): void {
    const verb = this.crashVerbs[Math.floor(Math.random() * this.crashVerbs.length)];
    this.killFeedItems.unshift({
      killEvent: {
        killerId: '',
        killerName: this.username,
        killerTeam: this.team,
        victimId: 'self',
        victimName: '',
        victimTeam: this.team,
        weapon: 'crash',
      },
      age: 0,
      verb,
    });
    if (this.killFeedItems.length > 6) this.killFeedItems.pop();
  }

  private updateMissileArrows(missilePositions: THREE.Vector3[]): void {
    const hud = document.getElementById('hud')!;
    const hw = window.innerWidth / 2;
    const hh = window.innerHeight / 2;
    const margin = 60;

    // Ensure we have enough arrow elements
    while (this.missileArrows.length < missilePositions.length) {
      const arrow = document.createElement('div');
      arrow.className = 'missile-arrow';
      hud.appendChild(arrow);
      this.missileArrows.push(arrow);
    }

    // Update each arrow
    for (let i = 0; i < this.missileArrows.length; i++) {
      const arrow = this.missileArrows[i];
      if (i >= missilePositions.length) {
        arrow.style.display = 'none';
        continue;
      }

      const missilePos = missilePositions[i];
      const screenPos = missilePos.clone().project(this.chaseCamera.camera);

      // If missile is on screen, hide arrow
      if (screenPos.z < 1 && Math.abs(screenPos.x) < 0.8 && Math.abs(screenPos.y) < 0.8) {
        arrow.style.display = 'none';
        continue;
      }

      arrow.style.display = 'block';

      let sx = screenPos.x;
      let sy = -screenPos.y;
      if (screenPos.z > 1) { sx = -sx; sy = -sy; }

      // Normalize to edge
      const len = Math.sqrt(sx * sx + sy * sy);
      if (len > 0.001) { sx /= len; sy /= len; }

      const px = hw + sx * (hw - margin);
      const py = hh + sy * (hh - margin);
      const angle = Math.atan2(sy, sx) - Math.PI / 2;

      arrow.style.left = `${px - 12}px`;
      arrow.style.top = `${py - 10}px`;
      arrow.style.transform = `rotate(${angle}rad)`;
    }
  }

  private initSettings(): void {
    const overlay = document.getElementById('settings-overlay')!;
    let open = false;

    // Game code — will be set by server via lobby events
    const codeEl = document.getElementById('game-code')!;
    const code = codeEl.textContent ?? '------';

    // ESC toggle
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Escape' && !this.rebindingAction) {
        open = !open;
        overlay.style.display = open ? 'flex' : 'none';
        if (open) document.exitPointerLock();
      }
    });

    // Master volume (everything except music)
    const masterSlider = document.getElementById('master-volume') as HTMLInputElement;
    masterSlider.addEventListener('input', () => {
      this.audio.setMasterVolume(parseInt(masterSlider.value) / 100);
    });

    // Music volume (only music)
    const musicSlider = document.getElementById('music-volume') as HTMLInputElement;
    musicSlider.addEventListener('input', () => {
      this.audio.setMusicVolume(parseInt(musicSlider.value) / 100);
    });

    // Toggle controls
    const toggleBtn = document.getElementById('toggle-controls')!;
    const controlsList = document.getElementById('controls-list')!;
    toggleBtn.addEventListener('click', () => {
      const showing = controlsList.style.display === 'block';
      controlsList.style.display = showing ? 'none' : 'block';
      toggleBtn.textContent = showing ? 'Show Controls' : 'Hide Controls';
    });

    // Build keybind table
    this.buildKeybindTable();

    // Switch team
    document.getElementById('switch-team-btn')!.addEventListener('click', () => {
      const newRole = this.role === 'f16' ? 'shahed' as const : 'f16' as const;

      // Remove old model from scene
      const oldMesh = this.flightController.mesh;
      this.gameScene.scene.remove(oldMesh);

      // Create new model
      const newModel = newRole === 'shahed'
        ? this.assets.shahed.clone()
        : this.assets.f16.clone();
      newModel.name = 'player';
      this.gameScene.scene.add(newModel);

      // New flight controller with new model
      this.role = newRole;
      this.flightController = new FlightController(newModel, newRole);
      this.flightController.resetOnRespawn();

      // Update wing trails
      const wingSpan = newRole === 'shahed' ? 5.25 : 3;
      const trailZ = newRole === 'shahed' ? 3 : 5;
      this.wingTrails.dispose();
      this.wingTrails = new WingTrails(
        this.gameScene.scene,
        new THREE.Vector3(-wingSpan, 0, trailZ),
        new THREE.Vector3(wingSpan, 0, trailZ),
        0.4,
      );

      // Reset scores
      this.combatSystem.scores.drone = 0;
      this.combatSystem.scores.fighter = 0;

      // Toggle HUD elements
      const crosshair = this.crosshair;
      const boostBar = document.getElementById('boost-bar-container')!;
      const boostLabel = document.getElementById('boost-label')!;
      const gunBar = document.getElementById('gun-bar-container')!;
      const gunLabel = document.getElementById('gun-label')!;
      if (newRole === 'shahed') {
        crosshair.style.display = 'none';
        gunBar.style.display = 'none';
        gunLabel.style.display = 'none';
        boostBar.style.display = 'block';
        boostLabel.style.display = 'block';
        this.flareCounter.style.display = 'flex';
        this.missileHud.style.display = 'none';
      } else {
        crosshair.style.display = 'block';
        gunBar.style.display = 'block';
        gunLabel.style.display = 'block';
        boostBar.style.display = 'none';
        boostLabel.style.display = 'none';
        this.flareCounter.style.display = 'none';
        this.missileHud.style.display = 'flex';
      }

      // Respawn at correct position
      this.playerAlive = true;
      this.respawnPlayer();

      overlay.style.display = 'none';
      open = false;
      this.touchControls?.updateRole(newRole);
      console.log(`[SKYFALL] Switched to ${newRole}`);
    });

    // Copy code
    document.getElementById('copy-code-btn')!.addEventListener('click', () => {
      navigator.clipboard.writeText(code);
      document.getElementById('copy-code-btn')!.textContent = 'Copied!';
      setTimeout(() => {
        document.getElementById('copy-code-btn')!.textContent = 'Copy Code';
      }, 2000);
    });
  }

  private rebindingAction: keyof KeyBindings | null = null;

  private buildKeybindTable(): void {
    const table = document.getElementById('keybind-table')!;
    const actionLabels: Record<keyof KeyBindings, string> = {
      forward: 'Pitch Down',
      backward: 'Pitch Up',
      left: 'Turn Left',
      right: 'Turn Right',
      up: 'Boost / Fire Gun',
      down: 'Brake',
      boost: 'Barrel Roll Right',
      flare: 'Barrel Roll Left',
      lockMode: 'Missile Lock (F-16)',
      deployFlare: 'Deploy Flare (Drone)',
    };

    // Clear existing rows except header
    while (table.rows.length > 1) table.deleteRow(1);

    for (const [action, label] of Object.entries(actionLabels)) {
      const row = table.insertRow();
      const nameCell = row.insertCell();
      nameCell.textContent = label;

      const keyCell = row.insertCell();
      const btn = document.createElement('button');
      btn.className = 'settings-btn';
      btn.style.padding = '4px 12px';
      btn.style.width = 'auto';
      btn.style.display = 'inline-block';
      btn.textContent = keyCodeToLabel(this.inputHandler.bindings[action as keyof KeyBindings]);

      btn.addEventListener('click', () => {
        btn.textContent = 'Press a key...';
        btn.style.color = '#0af';
        this.rebindingAction = action as keyof KeyBindings;

        const handler = (e: KeyboardEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.code === 'Escape') {
            btn.textContent = keyCodeToLabel(this.inputHandler.bindings[action as keyof KeyBindings]);
            btn.style.color = '';
          } else {
            this.inputHandler.rebind(action as keyof KeyBindings, e.code);
            btn.textContent = keyCodeToLabel(e.code);
            btn.style.color = '';
          }
          this.rebindingAction = null;
          window.removeEventListener('keydown', handler, true);
        };
        window.addEventListener('keydown', handler, true);
      });

      keyCell.appendChild(btn);
    }

    // Add non-rebindable entries
    const fixed = [
      ['Reverse Camera', 'T'],
      ['Settings', 'Escape'],
    ];
    for (const [label, key] of fixed) {
      const row = table.insertRow();
      row.insertCell().textContent = label;
      row.insertCell().textContent = key;
    }
  }

  private onResize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h);
    this.chaseCamera.onResize(w / h);
  }

  private setupReconnectOverlay(): void {
    if (!this.socketClient) return;

    // Create overlay (hidden by default)
    const overlay = document.createElement('div');
    overlay.id = 'reconnect-overlay';
    overlay.style.cssText = `
      position:fixed;inset:0;display:none;align-items:center;justify-content:center;
      flex-direction:column;background:rgba(0,0,0,0.7);z-index:9999;
      font-family:monospace;color:white;gap:20px;
    `;

    const spinner = document.createElement('div');
    spinner.style.cssText = `
      width:40px;height:40px;border:3px solid rgba(255,255,255,0.3);
      border-top-color:white;border-radius:50%;
      animation:skyfall-spin 0.8s linear infinite;
    `;

    const style = document.createElement('style');
    style.textContent = '@keyframes skyfall-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);

    const text = document.createElement('div');
    text.style.cssText = 'font-size:18px;letter-spacing:4px;text-transform:uppercase;';
    text.textContent = 'RECONNECTING...';

    const subtext = document.createElement('div');
    subtext.style.cssText = 'font-size:12px;opacity:0.6;';
    subtext.textContent = '';

    overlay.appendChild(spinner);
    overlay.appendChild(text);
    overlay.appendChild(subtext);
    document.body.appendChild(overlay);

    this.socketClient.onReconnecting((attempt) => {
      overlay.style.display = 'flex';
      subtext.textContent = `Attempt ${attempt} of 5`;
    });

    this.socketClient.onReconnected(() => {
      overlay.style.display = 'none';
    });

    this.socketClient.onReconnectFailed(() => {
      spinner.style.display = 'none';
      text.textContent = 'CONNECTION LOST';
      subtext.textContent = '';

      const btn = document.createElement('button');
      btn.textContent = 'RETURN TO LOBBY';
      btn.style.cssText = `
        padding:10px 24px;font-family:monospace;font-size:14px;letter-spacing:2px;
        background:#4af;color:black;border:none;cursor:pointer;margin-top:10px;
      `;
      btn.onclick = () => window.location.reload();
      overlay.appendChild(btn);
    });
  }

  dispose(): void {
    this.running = false;
    this.stateEmitter?.stop();
    this.remoteProjectiles?.dispose();
    this.remotePlayerManager?.dispose();
    this.socketClient?.disconnect();
    this.touchControls?.dispose();
    this.renderer.dispose();
  }
}
